// src/components/Intranet/Components/usuario/DocumentosUsuario.jsx
import React, { useEffect, useState } from "react";
import api from "../../../../api";
import { API_BASE } from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId, isSessionSuperAdmin, getSessionEmpresaId } from "../../../../utils/SessionUser";
import { ENDPOINTS as SEC_ENDPOINTS } from "../../../Seguridad/Utils/Endpoints";
import "../../Styles/index.css";

const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');

const DocumentosUsuario = ({ onVolver }) => {
    // Estado de datos
    const [allAreas, setAllAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [allEmployees, setAllEmployees] = useState([]);
    const [allPersonas, setAllPersonas] = useState([]);
    const [allGestorRelations, setAllGestorRelations] = useState([]);

    // Carga inicial
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const userId = getSessionUserId();
                const isSuper = isSessionSuperAdmin();
                const myEmpresaId = getSessionEmpresaId();

                const [resAreas, resEmpleados, resPersonas, resGestor] = await Promise.all([
                    api.get(ENDPOINTS.areasNegocios),
                    api.get(ENDPOINTS.empleados),
                    api.get(ENDPOINTS.personas),
                    api.get(ENDPOINTS.gestorDocumental)
                ]);

                const rawAreas = resAreas.data || [];
                const filteredAreas = rawAreas.filter(a => {
                    if (isSuper) return true;
                    const empId = a.idEmpresa || a.IdEmpresa;
                    return Number(empId) === Number(myEmpresaId) || !empId || Number(empId) === 0;
                });

                setAllAreas(filteredAreas);
                setAllEmployees(resEmpleados.data);
                setAllPersonas(resPersonas.data);
                setAllGestorRelations(resGestor.data);

                // 3. Set Selection: Prioritize current company, then first available
                if (filteredAreas.length > 0) {
                    const defaultArea = filteredAreas.find(a =>
                        Number(a.idEmpresa || a.IdEmpresa) === Number(myEmpresaId)
                    ) || filteredAreas[0];
                    setSelectedArea(defaultArea);
                }

                setLoading(false);

            } catch (err) {
                console.error("Error inicializando documentos usuario:", err);
                setError("Error al cargar documentos.");
                setLoading(false);
            }
        };

        if (ENDPOINTS.documentos) {
            init();
        } else {
            setError("Falta configuración de ENDPOINTS.");
            setLoading(false);
        }

    }, []);

    // Cargar documentos cuando cambia el área seleccionada
    useEffect(() => {
        if (!selectedArea) return;

        const loadDocs = async () => {
            try {
                const userId = getSessionUserId();
                const selectedAreaId = selectedArea.idAreaNegocio || selectedArea.IdAreaNegocio;

                // Usar el endpoint optimizado del dashboard que ya trae Joins y filtrado por empresa/visibilidad
                const res = await api.get(ENDPOINTS.documentosDashboardByUsuarioId(userId));

                // Filtrar solo por el área seleccionada en el sidebar
                const docsFiltrados = res.data.filter(doc =>
                    (doc.idAreaNegocio || doc.IdAreaNegocio) === selectedAreaId
                );

                setDocumentos(docsFiltrados);

            } catch (err) {
                console.error("Error cargando documentos:", err);
            }
        };

        loadDocs();
    }, [selectedArea]);

    const handleAreaClick = (area) => {
        const currentId = selectedArea ? (selectedArea.idAreaNegocio || selectedArea.IdAreaNegocio) : null;
        const targetId = area.idAreaNegocio || area.IdAreaNegocio;

        // Si ya está seleccionada la misma área, no hacer nada
        if (currentId === targetId) {
            return;
        }
        setSelectedArea(area);
        setDocumentos([]);
    };

    const formatFecha = (f) => {
        if (!f) return "";
        const date = new Date(f);
        return isNaN(date.getTime()) ? "" : date.toLocaleDateString();
    };

    if (loading) return <div className="intra-loading">Cargando documentos...</div>;
    if (error) return <div className="intra-error-panel">{error}</div>;

    return (
        <div className="intra-docs-page">
            <header className="intra-docs-header">
                <div>
                    <h1 className="intra-docs-title">Gestión Documental</h1>
                    <p className="intra-docs-subtitle">
                        Repositorio centralizado de documentos por áreas de negocio.
                    </p>
                </div>
                {onVolver && (
                    <button className="intra-button-ghost" onClick={onVolver}>
                        ← Volver
                    </button>
                )}
            </header>

            <section className="intra-docs-layout">

                {/* SIDEBAR: TODAS LAS AREAS */}
                <aside className="intra-docs-users-panel">
                    <h2 className="intra-docs-panel-title">Áreas de Negocio</h2>
                    <p className="intra-docs-panel-subtitle">Explora documentos por área.</p>

                    <div className="intra-docs-users-list">
                        {allAreas.map(area => {
                            const areaId = area.idAreaNegocio || area.IdAreaNegocio;
                            const currentId = selectedArea ? (selectedArea.idAreaNegocio || selectedArea.IdAreaNegocio) : null;
                            const isSelected = areaId === currentId;

                            return (
                                <div
                                    key={areaId}
                                    className={`intra-docs-user-row ${isSelected ? 'intra-docs-user-row--selected' : ''}`}
                                    onClick={() => handleAreaClick(area)}
                                >
                                    <span className="intra-docs-user-name">{area.nombre || area.Nombre || area.descripcion || area.Descripcion}</span>
                                </div>
                            );
                        })}
                    </div>
                </aside>

                {/* MAIN: DOCUMENTOS */}
                <main className="intra-docs-main-panel">
                    <div className="intra-docs-main-header">
                        <div className="intra-docs-main-left">
                            <span className="intra-docs-main-title">
                                {selectedArea ? (selectedArea.nombre || selectedArea.Nombre || selectedArea.descripcion || selectedArea.Descripcion) : 'Selecciona un área'}
                            </span>
                        </div>
                        {selectedArea && (
                            <input
                                type="text"
                                placeholder="Buscar por título o autor..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    border: '1px solid #cbd5e1',
                                    outline: 'none',
                                    width: '250px',
                                    fontSize: '0.85rem'
                                }}
                            />
                        )}
                    </div>

                    <div className="intra-docs-docs-grid">
                        {documentos.filter(doc => {
                            if (!searchTerm) return true;
                            const term = searchTerm.toLowerCase();
                            const title = (doc.titulo || doc.Titulo || "").toLowerCase();

                            // Resolver autor
                            const docIdGestor = doc.idEmpleadoGestor || doc.IdEmpleadoGestor;
                            let authorName = (doc.gestorNombre || doc.GestorNombre || "").toLowerCase();
                            if (!authorName || authorName === "undefined undefined") {
                                const emp = allEmployees.find(e => (e.idEmpleado || e.IdEmpleado) === docIdGestor);
                                const pers = emp ? allPersonas.find(p => (p.idPersona || p.IdPersona) === (emp.idPersona || emp.IdPersona)) : null;
                                if (pers) {
                                    authorName = (pers.nombreCompleto || pers.NombreCompleto || `${pers.nombre || pers.Nombre} ${pers.apellidoPaterno || pers.ApellidoPaterno}`).toLowerCase();
                                }
                            }

                            return title.includes(term) || (authorName && authorName.includes(term));
                        }).map(doc => (
                            <article
                                key={doc.idDocumento || doc.IdDocumento}
                                className="intra-docs-doc-card"
                                onClick={() => {
                                    const url = doc.urlDocumento || doc.UrlDocumento;
                                    if (url) {
                                        window.open(url.startsWith('http') ? url : `${SERVER_URL}${url}`, '_blank');
                                    }
                                }}
                                style={{ cursor: 'pointer', position: 'relative' }}
                            >
                                {(() => {
                                    const docIdGestor = doc.idEmpleadoGestor || doc.IdEmpleadoGestor;

                                    // 1. Prioridad: Datos ya mapeados en el objeto doc
                                    const rawName = doc.gestorNombre || doc.GestorNombre;
                                    if (rawName && rawName !== "undefined undefined") return (
                                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.6rem', fontStyle: 'italic' }}>
                                            Por: {rawName}
                                        </div>
                                    );

                                    // 2. Fallback: Buscar y concatenar Nombre + AP + AM
                                    const emp = allEmployees.find(e => (e.idEmpleado || e.IdEmpleado) === docIdGestor);
                                    const pers = emp ? allPersonas.find(p => (p.idPersona || p.IdPersona) === (emp.idPersona || emp.IdPersona)) : null;

                                    if (pers) {
                                        const nombrePartes = [
                                            pers.nombre || pers.Nombre,
                                            pers.apellidoPaterno || pers.ApellidoPaterno,
                                            pers.apellidoMaterno || pers.ApellidoMaterno
                                        ].filter(part => part && part !== "undefined");

                                        let nombreCalculado = pers.nombreCompleto || pers.NombreCompleto;
                                        if (!nombreCalculado || nombreCalculado === "undefined undefined") {
                                            nombreCalculado = nombrePartes.join(" ").trim();
                                        }

                                        if (nombreCalculado && nombreCalculado.length > 2) {
                                            return (
                                                <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.6rem', fontStyle: 'italic' }}>
                                                    Por: {nombreCalculado}
                                                </div>
                                            );
                                        }
                                    }
                                    return null;
                                })()}

                                <h3 className="intra-docs-doc-title">
                                    {((doc.titulo || doc.Titulo) && (doc.titulo || doc.Titulo).trim() !== "" && (doc.titulo || doc.Titulo) !== "Sin Título")
                                        ? (doc.titulo || doc.Titulo)
                                        : (doc.fechaFormateada || formatFecha(doc.fecha || doc.Fecha))}
                                </h3>

                                <div className="intra-docs-doc-footer">
                                    {((doc.titulo || doc.Titulo) && (doc.titulo || doc.Titulo).trim() !== "" && (doc.titulo || doc.Titulo) !== "Sin Título")
                                        ? <span>{doc.fechaFormateada || formatFecha(doc.fecha || doc.Fecha)}</span>
                                        : <span></span>}
                                </div>
                            </article>
                        ))}
                        {documentos.length === 0 && selectedArea && (
                            <div className="intra-docs-empty">No hay documentos visibles en esta área.</div>
                        )}
                    </div>
                </main>
            </section>
        </div>
    );
};

export default DocumentosUsuario;
